"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/telemedicine/ - tempat berkas INI
   berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./telemedicine                -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import {
  FOTO,
  KETENTUAN,
  LANGKAH,
  TAUTAN_RESERVASI,
  TAUTKAN_DELIVERY,
  TELEMEDICINE,
} from "./telemedicine";

/* ============================================================
   TELEMEDICINE - /layanan/telemedicine

   Sebelum berkas ini ada, alamat ini 404 - padahal sudah ditunjuk
   menu Header dan akordeon beranda.

   ============================================================
   YANG DITAMBAHKAN DIBANDING HALAMAN LAMA

   Halaman lama memuat satu kalimat, dua nomor, satu tombol, dan satu
   poster. Pertanyaan yang membuat orang ragu memakai telemedicine -
   lewat aplikasi apa, berapa biayanya, bisa dapat resep atau tidak -
   tidak dijawab sama sekali.

   Halaman ini menyediakan TEMPAT untuk jawaban itu lewat KETENTUAN di
   telemedicine.js, tapi isinya sengaja dibiarkan kosong sampai RS
   yang mengisinya. Selama kosong, bagiannya tidak dirender - halaman
   tetap rapi tanpa judul menggantung.

   Yang bisa ditambahkan tanpa menunggu siapa pun adalah TAUTAN KE
   DELIVERY OBAT di dasar halaman. Konsultasi daring lalu obat diantar
   ke rumah adalah satu alur utuh yang membuat pasien tidak perlu
   datang sama sekali, dan kedua halamannya kini sudah ada.

   ============================================================
   TOMBOL UTAMA: PERIKSA TUJUANNYA

   Situs lama punya tombol "Klik untuk Reservasi Sekarang!" yang tidak
   bisa saya lihat tujuannya. Di sini ia diarahkan ke WhatsApp
   Customer Service - tebakan yang masuk akal, tapi tetap tebakan.

   Kalau ternyata mengarah ke platform reservasi tersendiri, isi
   TAUTAN_RESERVASI di telemedicine.js dan tombol ini akan memakai
   alamat itu.

   ============================================================
   className ditulis satu baris - lihat peringatan hydration di
   Facility.jsx. Jangan pakai string multi-baris di berkas ini.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Halaman induk /layanan sudah ada, jadi remah rotinya boleh jadi
   tautan. Pola dan alasannya sama dengan halaman layanan lain. */
const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Telemedicine", en: "Telemedicine" },
  sub: {
    id: "Konsultasi dengan dokter secara daring, tanpa perlu datang ke rumah sakit. Reservasi dilakukan lewat Customer Service.",
    en: "Consult a doctor online, without visiting the hospital. Bookings are made through Customer Service.",
  },

  reservasi: { id: "Reservasi Sekarang", en: "Book Now" },
  teleponSaja: { id: "Telepon", en: "Call" },

  nomorJudul: { id: "Reservasi dan Informasi", en: "Booking and Information" },
  nomorSub: {
    id: "Jadwal dokter, biaya, dan ketentuan konsultasi dapat ditanyakan lewat Customer Service.",
    en: "Ask Customer Service about doctor schedules, fees, and consultation requirements.",
  },
  chatWa: { id: "Chat WhatsApp", en: "Chat on WhatsApp" },
  telepon: { id: "Telepon", en: "Call" },

  ketentuanJudul: { id: "Ketentuan Layanan", en: "Service Details" },
  langkahJudul: { id: "Cara Reservasi", en: "How to Book" },

  deliveryJudul: { id: "Obatnya bisa diantar", en: "We can deliver your medicines" },
  deliveryIsi: {
    id: "Resep dari konsultasi daring dapat ditebus di Apotek RS JMC dan diantar ke alamat Anda, sehingga Anda tidak perlu datang sama sekali.",
    en: "Prescriptions from your online consultation can be filled at the JMC pharmacy and delivered to your address, so you do not need to visit at all.",
  },
  deliveryTombol: { id: "Lihat layanan Delivery Obat", en: "See Medicine Delivery" },

  lihatLayanan: { id: "Lihat layanan lainnya", en: "See other services" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

export default function TelemedicineContent() {
  const { lang, t } = useLanguage();

  /* Poster bisa gagal dimuat - nama berkasnya diisi manual di
     telemedicine.js dan ekstensinya mudah meleset. Kalau gagal,
     seluruh bagiannya disembunyikan, bukan menyisakan kotak kosong
     berikon gambar rusak. */
  const [fotoGagal, setFotoGagal] = useState(false);
  const adaFoto = Boolean(FOTO?.src) && !fotoGagal;

  const utama = TELEMEDICINE.telepon.find((n) => n.utama) ?? TELEMEDICINE.telepon[0];

  /* WhatsApp hanya MENGISI kolom ketik, tidak mengirim - pasien masih
     bisa menyunting pesannya sebelum menekan kirim. */
  function tautanWa(n) {
    const pesan = pilihTeks(n.pesanWa, lang);
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pesan)}`;
  }

  /* Kalau RS punya platform reservasi sendiri, alamat itu yang
     dipakai; kalau tidak, jatuh ke WhatsApp Customer Service.
     `luar` menentukan apakah tautannya dibuka di tab baru. */
  const adaTautanLuar = Boolean(TAUTAN_RESERVASI);
  const tujuanUtama = adaTautanLuar ? TAUTAN_RESERVASI : (utama.wa ? tautanWa(utama) : `tel:${utama.tel}`);
  const bukaTabBaru = adaTautanLuar || Boolean(utama.wa);

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA: DUA KOLOM ----------
         Di layar sempit keduanya menumpuk sendiri: teks dan tombol
         dulu, poster menyusul di bawahnya. */}
      <div className="bg-[var(--c-tint)]">
        <div className="mx-auto px-4 py-12 md:py-14" style={{ maxWidth: LEBAR_HALAMAN }}>
          <div className="grid items-center gap-10" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))" }}>

            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]">
                {pilihTeks(TEKS.eyebrow, lang)}
              </p>

              <h1 className="mt-2.5 text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ fontSize: "clamp(30px, 4.4vw, 44px)" }}>
                {pilihTeks(TEKS.judul, lang)}
              </h1>

              <p className="mt-4 max-w-[520px] text-[15.5px] leading-[1.7] text-[var(--c-body)]">
                {pilihTeks(TEKS.sub, lang)}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={tujuanUtama}
                  target={bukaTabBaru ? "_blank" : undefined}
                  rel={bukaTabBaru ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-3 rounded-[12px] bg-[var(--c-brand)] px-6 py-4 text-[16px] font-bold text-white shadow-sm transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                >
                  {/* Ikon menyesuaikan tujuannya, supaya tombolnya
                     tidak berbohong: lambang WhatsApp pada tautan yang
                     ternyata membuka formulir web akan terasa seperti
                     jebakan kecil. */}
                  {!adaTautanLuar && utama.wa ? <IkonWhatsApp /> : <IkonKalender />}
                  {pilihTeks(TEKS.reservasi, lang)}
                </a>

                {/* Nomor teleponnya sendiri yang jadi tulisan tombol,
                   bukan kata "Hubungi kami". Orang yang membuka dari
                   komputer perlu MEMBACA angkanya untuk ditekan di
                   ponsel lain. */}
                <a
                  href={`tel:${utama.tel}`}
                  className="inline-flex items-center gap-2.5 rounded-[12px] border border-[var(--c-brand-300)] bg-[var(--c-surface)] px-6 py-4 text-[16px] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                >
                  <IkonTelepon />
                  {utama.tampil}
                </a>
              </div>
            </div>

            {adaFoto && (
              <div className="min-w-0">
                <div className="overflow-hidden rounded-2xl bg-[var(--c-surface)] shadow-sm ring-1 ring-[var(--c-brand-line)]">
                  <Image
                    src={FOTO.src}
                    width={1200}
                    height={1600}
                    sizes="(min-width: 1024px) 520px, 92vw"
                    alt={pilihTeks(FOTO.alt, lang)}
                    onError={() => setFotoGagal(true)}
                    className="h-auto w-full"
                  />
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="mx-auto px-4 py-12 md:py-16" style={{ maxWidth: LEBAR_HALAMAN }}>

        {/* ---------- NOMOR ---------- */}
        <section>
          <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
            {pilihTeks(TEKS.nomorJudul, lang)}
          </h2>
          <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
            {pilihTeks(TEKS.nomorSub, lang)}
          </p>

          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {TELEMEDICINE.telepon.map((n) => (
              <li key={n.tel} className="rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)]">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-[var(--c-muted)]">
                  {pilihTeks(n.label, lang)}
                </p>
                <p className="mt-1.5 text-lg font-bold text-[var(--c-ink)]">{n.tampil}</p>

                <div className="mt-3.5 flex flex-wrap gap-2">
                  {n.wa && (
                    <a
                      href={tautanWa(n)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--c-brand)] px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                    >
                      <IkonWhatsApp kecil />
                      {pilihTeks(TEKS.chatWa, lang)}
                    </a>
                  )}

                  <a
                    href={`tel:${n.tel}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-bold text-[var(--c-brand)] ring-1 ring-[var(--c-line)] transition-colors hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <IkonTelepon kecil />
                    {pilihTeks(TEKS.telepon, lang)}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ---------- KETENTUAN ----------
           Muncul hanya kalau KETENTUAN diisi. Selama kosong, seluruh
           bagian ini tidak ada. */}
        {KETENTUAN.length > 0 && (
          <section className="mt-12 md:mt-16">
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.ketentuanJudul, lang)}
            </h2>

            {/* <dl>, bukan tabel. Pasangan label-nilai memang itulah
               tempatnya, dan pembaca layar mengumumkannya sebagai
               pasangan keterangan. */}
            <dl className="mt-5 overflow-hidden rounded-2xl ring-1 ring-[var(--c-line)]">
              {KETENTUAN.map((k, i) => (
                <div key={i} className={`flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 bg-[var(--c-surface)] px-5 py-4 ${i > 0 ? "border-t border-[var(--c-line)]" : ""}`}>
                  <dt className="text-[14px] font-semibold text-[var(--c-muted)]">{pilihTeks(k.label, lang)}</dt>
                  <dd className="text-[15px] font-bold text-[var(--c-ink)]">{pilihTeks(k.nilai, lang)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* ---------- CARA RESERVASI ---------- */}
        {LANGKAH.length > 0 && (
          <section className="mt-12 md:mt-16">
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.langkahJudul, lang)}
            </h2>

            {/* <ol>, bukan <ul>: urutannya bermakna, dan itulah yang
               memberi tahu pembaca layar "langkah 2 dari 4". Nomornya
               digambar sendiri sebagai lingkaran, jadi list-none. */}
            <ol className="mt-5 grid list-none gap-4 sm:grid-cols-2">
              {LANGKAH.map((l, i) => (
                <li key={i} className="flex gap-4 rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)]">
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--c-tint)] text-[14px] font-bold text-[var(--c-brand)] ring-1 ring-inset ring-[var(--c-brand-line-2)]">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold text-[var(--c-ink)]">{pilihTeks(l.judul, lang)}</p>
                    <p className="mt-1 text-[14.5px] leading-relaxed text-[var(--c-body)]">{pilihTeks(l.isi, lang)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ---------- TAUTAN KE DELIVERY OBAT ----------
           Menutup alurnya: konsultasi daring, resep, obat sampai di
           rumah. Setel TAUTKAN_DELIVERY jadi false kalau halaman itu
           dicabut - bagian ini hilang sendiri. */}
        {TAUTKAN_DELIVERY && (
          <section className="mt-12 md:mt-16">
            <div className="rounded-2xl bg-[var(--c-tint)] p-6 ring-1 ring-[var(--c-brand-line)] md:p-7">
              <p className="text-[16px] font-bold text-[var(--c-ink)] md:text-lg">
                {pilihTeks(TEKS.deliveryJudul, lang)}
              </p>
              <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
                {pilihTeks(TEKS.deliveryIsi, lang)}
              </p>
              <Link
                href="/layanan/delivery-obat"
                className="mt-4 inline-flex items-center gap-2 text-[15px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)]"
              >
                {pilihTeks(TEKS.deliveryTombol, lang)}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </section>
        )}

        <div className="mt-12 md:mt-16">
          <Link
            href="/layanan"
            className="inline-flex items-center gap-2 text-[15px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)]"
          >
            {pilihTeks(TEKS.lihatLayanan, lang)}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

      </div>

      <Footer />
    </div>
  );
}

function IkonWhatsApp({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.85 9.85 0 0 0 12.04 2zm0 18.14h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.22-8.24 8.22z" />
    </svg>
  );
}

function IkonTelepon({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IkonKalender() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={20} height={20} className="shrink-0">
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
